/**
 * 회원가입 직후 자동 로그인 — 이메일 인증 API가 Bearer 토큰을 요구하므로
 * 가입 성공 직후 /user/login + 프로필 조회로 세션을 맞춘다.
 */
import type { LoginDto, LoginResponseDto, UserProfileResponseDto } from '@/types/user';
import type { User } from '@/contexts/AuthContext';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import { saveLinkedAccountSession } from '@/utils/linkedAccountSessions';

function buildUserFromProfile(
	loginId: string,
	userProfile: UserProfileResponseDto,
	loginRes: LoginResponseDto
): User {
	const userRole = convertRoleNumberToString(userProfile.role);
	const userLicense = resolveLicenseFromProfile(userProfile);

	const organizationId = userProfile.organizationId != null ? String(userProfile.organizationId) : null;
	const organizationName = userProfile.organizationName?.trim() ?? '';
	const displayName =
		userProfile.realName && userProfile.realName.trim()
			? userProfile.realName.trim()
			: loginId;

	const currentAccountEntry = {
		accountId: `acc_${userProfile.userId}`,
		loginId,
		displayName,
		email: userProfile.email || '',
		profileImageUrl: userProfile.profileImageUrl,
		phoneNumber: userProfile.phoneNumber?.trim() ?? '',
		affiliation: userProfile.affiliation?.trim() ?? '',
		position: userProfile.position?.trim() ?? '',
		department: userProfile.department?.trim() ?? '',
		organizationId,
		organizationName,
		role: userRole,
		license: userLicense,
		isActive: true as const,
	};

	const guestExpirationDate =
		userRole === 'guest'
			? ((userProfile as { guestExpirationDate?: string | null }).guestExpirationDate ?? null)
			: null;

	return {
		userId: userProfile.userId,
		name: displayName,
		email: userProfile.email || '',
		profileImageUrl: userProfile.profileImageUrl,
		guestExpirationDate,
		isAuthenticated: true,
		currentAccount: {
			accountId: currentAccountEntry.accountId,
			loginId: currentAccountEntry.loginId,
			displayName: currentAccountEntry.displayName,
			email: currentAccountEntry.email,
			profileImageUrl: currentAccountEntry.profileImageUrl,
			phoneNumber: currentAccountEntry.phoneNumber,
			affiliation: currentAccountEntry.affiliation,
			position: currentAccountEntry.position,
			department: currentAccountEntry.department,
			organizationId: currentAccountEntry.organizationId,
			organizationName: currentAccountEntry.organizationName,
			role: currentAccountEntry.role,
			license: currentAccountEntry.license,
		},
		accounts: [currentAccountEntry],
	};
}

export async function tryAutoLoginAfterSignup(
	loginId: string,
	password: string,
	loginApi: (data: LoginDto) => Promise<LoginResponseDto>,
	getProfile: () => Promise<UserProfileResponseDto>,
	authLogin: (user: User) => void
): Promise<boolean> {
	const loginRes = await loginApi({ loginId, password });

	localStorage.setItem('medicrew_access_token', loginRes.accessToken);
	if (loginRes.refreshToken?.trim()) {
		localStorage.setItem('medicrew_refresh_token', loginRes.refreshToken.trim());
	} else {
		localStorage.removeItem('medicrew_refresh_token');
	}

	const userProfile = await getProfile();
	const userData = buildUserFromProfile(loginId, userProfile, loginRes);
	authLogin(userData);
	saveLinkedAccountSession(userData.currentAccount.accountId, {
		accessToken: loginRes.accessToken,
		...(loginRes.refreshToken?.trim() ? { refreshToken: loginRes.refreshToken.trim() } : {}),
	});

	return true;
}
